import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useStyleTemplate } from '../contexts/StyleTemplateContext';
import { DEFAULT_HEATMAP } from '../theme';
import { valueToHeatColor } from '../utils/heatmapColor';
import { HEATMAP_STYLE_GRADIENT } from '../utils/heatmapMatrix';
import { isDataImageUrl, isRemoteImageUrl, localizeImageUrl } from '../utils/localizeImageUrl';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { clusterPoints } from '../utils/heatmapPointCluster';
import {
  parseImageDensityPoints,
  listImageDensityPositionEntities,
  normalizeManualPositions,
  inferPositionKeyColumn,
  coalescePointsForMarkers,
} from '../utils/imageDensityPoints';
import {
  buildImageDensityTooltipContent,
  filterTooltipColumnsForRows,
} from '../utils/imageDensityTooltip';
import DensityPointModal from './chartBuilder/DensityPointModal';
import {
  describeTooltipFilter,
  filterRowsByTooltipValues,
  normalizeTooltipDetail,
  tooltipFilterValues,
} from '../utils/tooltipDetail';
import ImageDensityInteractiveCanvas from './chartBuilder/ImageDensityInteractiveCanvas';
import ImageDensityPositionTable from './chartBuilder/ImageDensityPositionTable';
import { useRegisterUnsavedSession } from '../contexts/UnsavedSessionContext';

export const DEFAULT_IMAGE_DENSITY = {
  renderMode: 'heatmap',
  heatmapStyle: 'smooth',
  intensity: 1,
  overlayOpacity: 1,
  blur: 7,
  pointSize: 1,
  separation: 0.55,
  backgroundDim: 0.72,
  showLegend: true,
  groupByColumn: '',
  weightColumn: '',
  selectedGroups: null,
  pointRadius: 14,
  clusterEnabled: false,
  clusterRadiusPx: 25,
  clusterMerge: 'count',
  clusterShowCount: false,
  clusterColorByCount: false,
  tooltipEnabled: false,
  tooltipColumns: [],
  tooltipDetail: null,
  positionMode: 'columns',
  positionKeyColumn: '',
  manualPositions: {},
  metricColumns: [],
  showMarkerLabels: false,
  markerEnumColumn: '',
  markerShape: 'circle',
  markerValueColumn: '',
  markerValueAggregation: 'first',
  markerLabelColumn: '',
  markerFill: 'value',
  markerFit: 'text',
  markerSize: 'medium',
  markerColor: '#ffffff',
  positionColumnsCommitted: false,
};

function HeatmapLegend({ colorByCount = false }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 0.25, flexShrink: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32 }}>
        {colorByCount ? 'Menos' : 'Baja'}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: 10,
          borderRadius: 1,
          background: HEATMAP_STYLE_GRADIENT,
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'right' }}>
        {colorByCount ? 'Más' : 'Alta'}
      </Typography>
    </Box>
  );
}

/**
 * Densidad de puntos (x,y) sobre imagen de referencia.
 */
export default function ImageDensityWidget({
  data = [],
  imageDensityConfig = {},
  columnDurationFormats,
  onConfigChange,
  allowPositionEdit = false,
  showImageControls = false,
  getAuthHeaders,
  unsavedSessionId,
  onPositionDraftChange,
}) {
  const localizeImageMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/localize-image', body, { headers }),
  });

  const { heatmap: heatmapPalette } = useStyleTemplate();
  const heatmapConfig = useMemo(
    () => (heatmapPalette ? { ...DEFAULT_HEATMAP, ...heatmapPalette } : { ...DEFAULT_HEATMAP }),
    [heatmapPalette]
  );

  const containerRef = useRef(null);
  const imageAreaRef = useRef(null);
  const loadedImageRef = useRef(null);
  const positionBackfillRef = useRef('');
  const [imgReady, setImgReady] = useState(false);
  const [localUrl, setLocalUrl] = useState('');
  const [urlDraft, setUrlDraft] = useState('');
  const [localizing, setLocalizing] = useState(false);
  const [localizeError, setLocalizeError] = useState('');
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const cfg = { ...DEFAULT_IMAGE_DENSITY, ...imageDensityConfig };
  const {
    backgroundImageUrl = '',
    xColumn = 'x',
    yColumn = 'y',
    imageWidth,
    imageHeight,
    renderMode = 'heatmap',
    heatmapStyle = 'smooth',
    intensity = 1,
    overlayOpacity = 1,
    blur = 7,
    pointSize = 1,
    separation = 0.55,
    backgroundDim = 0.72,
    showLegend = true,
    groupByColumn = '',
    weightColumn = '',
    selectedGroups,
    pointRadius = 14,
    clusterEnabled = false,
    clusterRadiusPx = 25,
    clusterMerge = 'count',
    clusterShowCount = false,
    clusterColorByCount = false,
    tooltipColumns = [],
    tooltipDetail = null,
    columnDateFormats = {},
    columnDateBuckets = {},
    metricColumns = [],
    positionMode = 'columns',
    positionKeyColumn = '',
    manualPositions = {},
    showMarkerLabels = false,
    markerShape = 'circle',
    markerValueColumn = '',
    markerValueAggregation = 'first',
    markerLabelColumn = '',
    markerFill = 'value',
    markerFit = 'text',
    markerSize = 'medium',
    markerColor = '#ffffff',
  } = cfg;

  const isManual = positionMode === 'manual';
  const [editMode, setEditMode] = useState(false);
  const [pendingPositions, setPendingPositions] = useState({});
  const [selectedUnplacedKey, setSelectedUnplacedKey] = useState('');

  const url = useMemo(() => {
    if (localUrl) return localUrl;
    if (isDataImageUrl(backgroundImageUrl)) return backgroundImageUrl;
    if (urlDraft.trim()) return urlDraft.trim();
    const raw = String(backgroundImageUrl || '').trim();
    if (raw.startsWith('/') && typeof window !== 'undefined') {
      return `${window.location.origin}${raw}`;
    }
    return backgroundImageUrl || '';
  }, [localUrl, backgroundImageUrl, urlDraft]);

  useEffect(() => {
    if (isDataImageUrl(backgroundImageUrl)) {
      setLocalUrl('');
      setUrlDraft('');
      setLocalizeError('');
    } else if (!localUrl) {
      setUrlDraft(backgroundImageUrl || '');
    }
  }, [backgroundImageUrl, localUrl]);

  useEffect(() => {
    let cancelled = false;
    const raw = String(backgroundImageUrl || '').trim();
    if (!raw || isDataImageUrl(raw) || localUrl) return undefined;
    if (!isRemoteImageUrl(raw)) return undefined;

    setLocalizing(true);
    setLocalizeError('');
    localizeImageUrl(raw, getAuthHeaders, (body, config) =>
      localizeImageMutation.mutateAsync({ body, headers: config.headers })
    )
      .then((dataUrl) => {
        if (!cancelled) {
          setLocalUrl(dataUrl);
          setUrlDraft('');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLocalizeError(err.response?.data?.error || err.message || 'Error al cargar imagen');
        }
      })
      .finally(() => {
        if (!cancelled) setLocalizing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [backgroundImageUrl, getAuthHeaders, localUrl]);

  const storedPositions = useMemo(
    () => normalizeManualPositions(manualPositions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(manualPositions || {})]
  );
  const activePositions = editMode ? pendingPositions : storedPositions;
  const resolvedPositionKeyColumn = useMemo(
    () =>
      inferPositionKeyColumn(
        data,
        isManual ? activePositions : storedPositions,
        positionKeyColumn
      ) || positionKeyColumn,
    [data, isManual, activePositions, storedPositions, positionKeyColumn]
  );

  const allPoints = useMemo(
    () =>
      parseImageDensityPoints(data, {
        xColumn,
        yColumn,
        weightColumn,
        groupByColumn,
        positionMode,
        positionKeyColumn: resolvedPositionKeyColumn,
        manualPositions: activePositions,
      }),
    [
      data,
      xColumn,
      yColumn,
      weightColumn,
      groupByColumn,
      positionMode,
      resolvedPositionKeyColumn,
      activePositions,
    ]
  );

  const positionEntities = useMemo(
    () =>
      isManual
        ? listImageDensityPositionEntities(data, {
            positionKeyColumn: resolvedPositionKeyColumn,
            manualPositions: activePositions,
          })
        : { all: [], unplaced: [], placed: {} },
    [isManual, data, resolvedPositionKeyColumn, activePositions]
  );

  const activeGroups = useMemo(() => {
    if (!groupByColumn || !Array.isArray(selectedGroups)) return null;
    if (selectedGroups.length === 0) return null;
    return new Set(selectedGroups.map(String));
  }, [groupByColumn, selectedGroups]);

  const filteredPoints = useMemo(() => {
    if (!activeGroups) return allPoints;
    return allPoints.filter((p) => activeGroups.has(p.group));
  }, [allPoints, activeGroups]);

  const points = useMemo(() => {
    const clustered = clusterPoints(filteredPoints, {
      enabled: clusterEnabled && !editMode,
      radiusPx: clusterRadiusPx,
      mergeMode: clusterMerge,
    });
    // En modo marcadores: un punto por cámara/posición para agregar el valor (suma, etc.).
    if (renderMode === 'markers' && !editMode) return coalescePointsForMarkers(clustered);
    return clustered;
  }, [filteredPoints, clusterEnabled, clusterRadiusPx, clusterMerge, editMode, renderMode]);

  const effectiveTooltipColumns = useMemo(
    () => filterTooltipColumnsForRows(tooltipColumns, data),
    [tooltipColumns, data]
  );

  const effectiveMetricColumns = useMemo(
    () => filterTooltipColumnsForRows(metricColumns, data),
    [metricColumns, data]
  );

  const normalizedTooltipDetail = useMemo(() => normalizeTooltipDetail(tooltipDetail), [tooltipDetail]);
  const tooltipActive = Boolean(normalizedTooltipDetail);

  const tooltipFilter = useMemo(() => {
    if (!normalizedTooltipDetail || !selectedPoint) return null;
    const column = normalizedTooltipDetail.filterColumn;
    const values = tooltipFilterValues(selectedPoint, column);
    return {
      rows: filterRowsByTooltipValues(data, column, values),
      label: describeTooltipFilter(column, values) || `${column}: sin valor`,
    };
  }, [normalizedTooltipDetail, selectedPoint, data]);

  const tooltipContent = useMemo(() => {
    if (!tooltipActive || !selectedPoint) return null;
    return buildImageDensityTooltipContent(selectedPoint, {
      tooltipColumns: effectiveTooltipColumns,
      metricColumns: effectiveMetricColumns,
      groupByColumn,
      weightColumn,
      positionKeyColumn: resolvedPositionKeyColumn,
      columnDurationFormats,
    });
  }, [
    tooltipActive,
    selectedPoint,
    effectiveTooltipColumns,
    effectiveMetricColumns,
    groupByColumn,
    weightColumn,
    resolvedPositionKeyColumn,
    columnDurationFormats,
  ]);

  const closeDetailModal = useCallback(() => {
    setDetailModalOpen(false);
    setSelectedPoint(null);
  }, []);

  const handleMainPointSelect = useCallback(
    (hit) => {
      if (!tooltipActive || !hit || editMode) return;
      setSelectedPoint(hit);
      setDetailModalOpen(true);
    },
    [tooltipActive, editMode]
  );

  const handleModalPointSelect = useCallback((hit) => {
    if (hit) setSelectedPoint(hit);
  }, []);

  const canEditPositions =
    allowPositionEdit && isManual && Boolean(resolvedPositionKeyColumn) && typeof onConfigChange === 'function';
  const showLabels =
    editMode || (renderMode !== 'markers' && (showMarkerLabels || (isManual && renderMode === 'points')));

  const startEdit = useCallback(() => {
    const pending = normalizeManualPositions(manualPositions);
    setPendingPositions(pending);
    const entities = listImageDensityPositionEntities(data, {
      positionKeyColumn: resolvedPositionKeyColumn,
      manualPositions: pending,
    });
    setSelectedUnplacedKey(entities.unplaced[0]?.key || '');
    setEditMode(true);
    closeDetailModal();
  }, [manualPositions, data, resolvedPositionKeyColumn, closeDetailModal]);

  const cancelEdit = useCallback(() => {
    setPendingPositions({});
    setSelectedUnplacedKey('');
    setEditMode(false);
  }, []);

  const confirmEdit = useCallback(() => {
    const img = loadedImageRef.current;
    onConfigChange?.({
      ...DEFAULT_IMAGE_DENSITY,
      ...imageDensityConfig,
      manualPositions: normalizeManualPositions(pendingPositions),
      imageWidth: img?.naturalWidth || imageWidth,
      imageHeight: img?.naturalHeight || imageHeight,
      positionKeyColumn: resolvedPositionKeyColumn || positionKeyColumn,
      positionColumnsCommitted: true,
    });
    setEditMode(false);
    setSelectedUnplacedKey('');
  }, [
    pendingPositions,
    imageWidth,
    imageHeight,
    onConfigChange,
    imageDensityConfig,
    resolvedPositionKeyColumn,
    positionKeyColumn,
  ]);

  useEffect(() => {
    if (!isManual || !onConfigChange || editMode) return;
    if (!Object.keys(storedPositions).length) return;
    const needsKey =
      Boolean(resolvedPositionKeyColumn) && resolvedPositionKeyColumn !== positionKeyColumn;
    const needsCommit = imageDensityConfig?.positionColumnsCommitted !== true;
    if (!needsKey && !needsCommit) return;
    const stamp = `${resolvedPositionKeyColumn || ''}|committed`;
    if (positionBackfillRef.current === stamp) return;
    positionBackfillRef.current = stamp;
    onConfigChange({
      ...DEFAULT_IMAGE_DENSITY,
      ...imageDensityConfig,
      ...(needsKey ? { positionKeyColumn: resolvedPositionKeyColumn } : {}),
      positionColumnsCommitted: true,
    });
  }, [
    isManual,
    editMode,
    storedPositions,
    resolvedPositionKeyColumn,
    positionKeyColumn,
    imageDensityConfig,
    onConfigChange,
  ]);

  useEffect(() => {
    onPositionDraftChange?.({
      editing: editMode,
      positions: editMode ? pendingPositions : null,
      positionKeyColumn: resolvedPositionKeyColumn,
    });
  }, [editMode, pendingPositions, resolvedPositionKeyColumn, onPositionDraftChange]);

  useEffect(
    () => () => {
      onPositionDraftChange?.({ editing: false, positions: null, positionKeyColumn: '' });
    },
    [onPositionDraftChange]
  );

  const positionsDirty = useMemo(
    () => JSON.stringify(pendingPositions) !== JSON.stringify(storedPositions),
    [pendingPositions, storedPositions]
  );

  useRegisterUnsavedSession(unsavedSessionId, editMode, {
    dirty: positionsDirty,
    message:
      'Se están editando las posiciones de los marcadores sobre la imagen. Guarda o descarta los cambios para continuar.',
    save: confirmEdit,
    discard: cancelEdit,
  });

  const handleDragPoint = useCallback(({ key, x, y }) => {
    if (!key || !Number.isFinite(x) || !Number.isFinite(y)) return;
    setPendingPositions((prev) => ({ ...prev, [key]: { x, y } }));
  }, []);

  const handlePlaceAt = useCallback(
    (coords) => {
      const key = selectedUnplacedKey;
      if (!key || !coords) return;
      const x = Number(coords.imageX);
      const y = Number(coords.imageY);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      const nextPositions = { ...pendingPositions, [key]: { x, y } };
      setPendingPositions(nextPositions);
      const remaining = listImageDensityPositionEntities(data, {
        positionKeyColumn: resolvedPositionKeyColumn,
        manualPositions: nextPositions,
      }).unplaced;
      setSelectedUnplacedKey(remaining[0]?.key || '');
    },
    [selectedUnplacedKey, pendingPositions, data, resolvedPositionKeyColumn]
  );

  const numberedPoints = useMemo(() => {
    if (!positionEntities.all.length) return points;
    const byKey = new Map(positionEntities.all.map((entity) => [entity.key, entity.markerNumber]));
    return points.map((point) => {
      const markerNumber = byKey.get(point.positionKey);
      return markerNumber != null ? { ...point, markerNumber } : point;
    });
  }, [points, positionEntities]);

  const chartProps = useMemo(
    () => ({
      points: numberedPoints,
      loadedImageRef,
      imgReady,
      url,
      imageWidth,
      imageHeight,
      renderMode,
      heatmapStyle,
      intensity,
      overlayOpacity,
      blur,
      pointSize,
      separation,
      backgroundDim,
      pointRadius,
      heatmapConfig,
      clusterEnabled: clusterEnabled && !editMode,
      clusterRadiusPx,
      clusterShowCount,
      clusterColorByCount,
      showMarkerLabels: showLabels,
      editMode,
      selectedPositionKey: selectedUnplacedKey,
      markerShape,
      markerValueColumn,
      markerValueAggregation,
      markerLabelColumn,
      markerFill,
      markerFit,
      markerSize,
      markerColor,
    }),
    [
      numberedPoints,
      imgReady,
      url,
      imageWidth,
      imageHeight,
      renderMode,
      heatmapStyle,
      intensity,
      overlayOpacity,
      blur,
      pointSize,
      separation,
      backgroundDim,
      pointRadius,
      heatmapConfig,
      clusterEnabled,
      clusterRadiusPx,
      clusterShowCount,
      clusterColorByCount,
      showLabels,
      editMode,
      selectedUnplacedKey,
      markerShape,
      markerValueColumn,
      markerValueAggregation,
      markerLabelColumn,
      markerFill,
      markerFit,
      markerSize,
      markerColor,
    ]
  );

  const patchConfig = useCallback(
    (patch) => {
      onConfigChange?.({ ...DEFAULT_IMAGE_DENSITY, ...imageDensityConfig, ...patch });
    },
    [onConfigChange, imageDensityConfig]
  );

  useEffect(() => {
    let cancelled = false;
    loadedImageRef.current = null;
    setImgReady(false);

    const src = String(url || '').trim();
    if (!src) {
      setImgReady(true);
      return undefined;
    }

    const finish = (img) => {
      if (cancelled) return;
      if (img && img.naturalWidth > 0) {
        loadedImageRef.current = img;
      }
      setImgReady(true);
    };

    const tryLoad = (useCors) => {
      const img = new Image();
      if (useCors && isRemoteImageUrl(src)) {
        img.crossOrigin = 'anonymous';
      }
      img.onload = () => finish(img);
      img.onerror = () => {
        if (cancelled) return;
        if (useCors && isRemoteImageUrl(src)) {
          tryLoad(false);
        } else {
          finish(null);
        }
      };
      img.src = src;
    };

    tryLoad(true);
    return () => {
      cancelled = true;
    };
  }, [url]);

  const needsBackground = Boolean(url);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!needsBackground || imgReady) {
      container.setAttribute('data-chart-visual-ready', 'true');
    } else {
      container.removeAttribute('data-chart-visual-ready');
    }
  }, [needsBackground, imgReady]);

  useEffect(() => {
    if (points.length === 0) return undefined;
    const id = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    return () => cancelAnimationFrame(id);
  }, [points.length, imgReady, url]);

  useEffect(() => {
    if (!tooltipActive) closeDetailModal();
  }, [tooltipActive, closeDetailModal]);

  useEffect(() => {
    closeDetailModal();
  }, [renderMode, clusterEnabled, closeDetailModal]);

  useEffect(() => {
    setSelectedPoint((prev) => {
      if (!prev) return prev;
      return (
        (points || []).find(
          (p) =>
            p === prev ||
            (p.positionKey && prev.positionKey && p.positionKey === prev.positionKey) ||
            (p.rowIndex === prev.rowIndex && p.x === prev.x && p.y === prev.y)
        ) || null
      );
    });
  }, [points]);

  useEffect(() => {
    if (detailModalOpen && !selectedPoint) setDetailModalOpen(false);
  }, [detailModalOpen, selectedPoint]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      setLocalUrl(dataUrl);
      setUrlDraft('');
      setLocalizeError('');
      patchConfig({ backgroundImageUrl: dataUrl });
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box
      className="chart-heatmap-host"
      sx={{ height: '100%', minHeight: 200, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75, overflow: 'hidden' }}
    >
      {showImageControls && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', flexShrink: 0 }}>
          <Button size="small" component="label" variant="outlined">
            Subir imagen
            <input type="file" accept="image/*" hidden onChange={onFile} />
          </Button>
          <TextField
            size="small"
            label="URL imagen"
            value={backgroundImageUrl?.startsWith('data:') ? '' : backgroundImageUrl || ''}
            placeholder="https://…"
            onChange={(e) => {
              setLocalUrl('');
              patchConfig({ backgroundImageUrl: e.target.value });
            }}
            sx={{ minWidth: 180, flex: 1 }}
          />
        </Box>
      )}

      {showImageControls && !url && (
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
          Sube o pega la URL de la imagen de referencia para ver la densidad.
        </Typography>
      )}

      {localizing && (
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          Cargando imagen…
        </Typography>
      )}
      {localizeError && (
        <Typography variant="caption" color="error" sx={{ flexShrink: 0 }}>
          {localizeError}
        </Typography>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            ref={containerRef}
            data-chart-needs-bg={needsBackground ? 'true' : undefined}
            sx={{
              flex: 1,
              minHeight: 180,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {editMode && (
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                  {(() => {
                    const selected = positionEntities.all.find((e) => e.key === selectedUnplacedKey);
                    if (selected) {
                      const n = selected.markerNumber ? `${selected.markerNumber}. ` : '';
                      return `Colocando «${n}${selected.label}». Haz clic en la imagen.`;
                    }
                    if (!positionEntities.all.length) return 'No hay elementos en la columna identificador.';
                    return 'Elige un elemento de la lista y haz clic en la imagen.';
                  })()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Button size="small" variant="outlined" onClick={cancelEdit}>
                    Cancelar
                  </Button>
                  <Button size="small" variant="contained" onClick={confirmEdit}>
                    Confirmar posición
                  </Button>
                </Box>
              </Box>
            )}
            <Box ref={imageAreaRef} sx={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
              {!editMode && canEditPositions ? (
                <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                  <Tooltip title="Editar posiciones de los marcadores">
                    <IconButton size="small" onClick={startEdit} sx={{ bgcolor: 'background.paper' }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : null}
              <ImageDensityInteractiveCanvas
                {...chartProps}
                selectedPoint={detailModalOpen ? selectedPoint : null}
                onSelectPoint={!editMode && tooltipActive ? handleMainPointSelect : undefined}
                interactive={!editMode && tooltipActive && points.length > 0}
                onPlaceAt={editMode ? handlePlaceAt : undefined}
                onDragPoint={editMode ? handleDragPoint : undefined}
                hitRadiusMultiplier={1.75}
              />
            </Box>
          </Box>

          <ImageDensityPositionTable
            open={editMode}
            anchorRef={imageAreaRef}
            entities={positionEntities.all}
            selectedKey={selectedUnplacedKey}
            onSelectKey={setSelectedUnplacedKey}
            positionKeyColumn={resolvedPositionKeyColumn}
            tooltipColumns={effectiveTooltipColumns}
            metricColumns={effectiveMetricColumns}
            columnDurationFormats={columnDurationFormats}
          />

          {tooltipActive && (
            <DensityPointModal
              open={detailModalOpen && Boolean(selectedPoint && tooltipFilter)}
              onClose={closeDetailModal}
              selectedPoint={selectedPoint}
              onSelectPoint={handleModalPointSelect}
              content={tooltipContent}
              detail={normalizedTooltipDetail}
              detailRows={tooltipFilter?.rows || []}
              filterLabel={tooltipFilter?.label || ''}
              columnDurationFormats={columnDurationFormats}
              columnDateFormats={columnDateFormats}
              columnDateBuckets={columnDateBuckets}
              chartProps={chartProps}
            />
          )}

          {showLegend !== false &&
            !editMode &&
            points.length > 0 &&
            (renderMode === 'heatmap' ||
              clusterColorByCount ||
              (renderMode === 'markers' && markerFill !== 'none')) && (
            <HeatmapLegend colorByCount={renderMode !== 'markers' && clusterEnabled && clusterColorByCount} />
          )}

          {showLegend !== false && !editMode && renderMode === 'points' && !clusterColorByCount && points.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: valueToHeatColor(1, heatmapConfig),
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Densidad de detecciones
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
